import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { UUID } from '@/types';
import { csrfToken } from '@/util/helpers/ajax-helpers';
import { completed } from '@/actions/helpers';
import { SharedRowRequestMetadata } from '@/features/publicDatasets/types';
import {
  SHARE_METADATA_CHANGED,
  SHARE_METADATA_LOADED,
} from '@/actions/constants';
import { AnyAction } from 'redux';

export type PublicDataset = {
  id: UUID;
  name: string;
};

const dispatchMetadata = <T>(
  dispatch: F<AnyAction>,
  data: T,
  changed: boolean,
) => {
  dispatch(
    completed(SHARE_METADATA_LOADED, {
      metadata: data,
    }),
  );
  dispatch(
    completed(SHARE_METADATA_CHANGED, {
      metadataChanged: changed,
    }),
  );
};

export const publicDatasetApi = createApi({
  reducerPath: 'publicDatasetApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/',
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json;charset=UTF-8');
      headers.set('x-requested-with', 'XMLHttpRequest');

      const token = csrfToken();
      if (token) headers.set('x-csrf-token', token);

      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAvailablePublicDatasets: builder.query<PublicDataset[], void>({
      query: () => `publicdatasets/list`,
    }),

    getRowRequestMetadata: builder.query<
      SharedRowRequestMetadata,
      { datasourceId: string | undefined; rowRequestId: Nullable<string> }
    >({
      query: ({ datasourceId, rowRequestId }) =>
        `/publicdatasets/${datasourceId}/${rowRequestId}/metadata`,

      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatchMetadata(dispatch, data, false);
      },
    }),

    savePublicDatasetMetadata: builder.mutation<
      SharedRowRequestMetadata,
      SharedRowRequestMetadata
    >({
      query(body: SharedRowRequestMetadata) {
        return {
          url: `/publicdatasets/${body.datasourceId}/${body.rowRequestId}/metadata`,
          method: 'POST',
          body,
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatchMetadata(dispatch, data, true);
      },
    }),
  }),
});

export const {
  useGetAvailablePublicDatasetsQuery,
  useSavePublicDatasetMetadataMutation,
  useGetRowRequestMetadataQuery,
  useLazyGetRowRequestMetadataQuery,
} = publicDatasetApi;
